/**
 * Global cache cleanup utility to ensure complete elimination of company data persistence
 * This provides aggressive cleanup strategies for new customers
 */

class CacheCleanup {  /**
   * Aggressively clear all company-related data from sessionStorage
   * @param {string} phoneNumber - Current phone number for logging
   * @param {string} source - Source of the cleanup call for debugging
   */
  static clearCompanyData(phoneNumber = 'unknown', source = 'unknown') {
    console.log(`🧹 CacheCleanup: Clearing company data - Phone: ${phoneNumber}, Source: ${source}`);
    
    // CRITICAL FIX: Check if this is a business number OR has API-sourced company data before clearing
    const isKnownCSVBusiness = this.shouldHaveCompanyData(phoneNumber);
    const hasAPICompanyData = sessionStorage.getItem('companyData') || sessionStorage.getItem('companyName');
    
    if (isKnownCSVBusiness || hasAPICompanyData) {
      console.log(`🛡️ CacheCleanup: PRESERVING company data for phone: ${phoneNumber}`, {
        csvBusiness: isKnownCSVBusiness,
        hasAPIData: !!hasAPICompanyData,
        source: hasAPICompanyData ? 'API/CSV' : 'CSV-only'
      });
      return; // Don't clear data for business numbers or API-sourced company data
    }
    
    // Clear all company-related sessionStorage items
    const companyKeys = ['companyData', 'companyName', 'selectedCompany'];
    companyKeys.forEach(key => {
      sessionStorage.removeItem(key);
    });
    
    // Additional cleanup with multiple attempts
    setTimeout(() => {
      companyKeys.forEach(key => {
        sessionStorage.removeItem(key);
      });
      console.log(`🧹 CacheCleanup: First retry cleanup completed for ${phoneNumber}`);
    }, 100);
    
    setTimeout(() => {
      companyKeys.forEach(key => {
        sessionStorage.removeItem(key);
      });
      console.log(`🧹 CacheCleanup: Second retry cleanup completed for ${phoneNumber}`);
    }, 500);
  }
  /**
   * Validate if sessionStorage is truly clean of company data
   * @returns {boolean} - True if clean, false if company data still exists
   */
  static validateCleanState() {
    const companyData = sessionStorage.getItem('companyData');
    const companyName = sessionStorage.getItem('companyName');
    const selectedCompany = sessionStorage.getItem('selectedCompany');
    const phoneNumber = sessionStorage.getItem('phoneNumber');
    
    // CRITICAL FIX: If this is a business number OR has API company data, company data is expected
    const isKnownCSVBusiness = this.shouldHaveCompanyData(phoneNumber);
    const hasAPICompanyData = companyData || companyName;
    
    if (isKnownCSVBusiness || hasAPICompanyData) {
      console.log('✅ CacheCleanup: Business customer - company data is expected and preserved', {
        csvBusiness: isKnownCSVBusiness,
        hasAPIData: !!hasAPICompanyData
      });
      return true; // For business customers or API-sourced data, having company data is correct
    }
    
    const isClean = !companyData && !companyName && !selectedCompany;
    
    if (!isClean) {
      console.warn('🚨 CacheCleanup: SessionStorage is NOT clean!', {
        companyData: companyData,
        companyName: companyName,
        selectedCompany: selectedCompany
      });
    } else {
      console.log('✅ CacheCleanup: SessionStorage is clean of company data');
    }
    
    return isClean;
  }

  /**
   * Force cleanup with validation - keeps trying until clean
   * @param {string} phoneNumber - Current phone number
   * @param {number} maxAttempts - Maximum cleanup attempts
   */
  static forceCleanupWithValidation(phoneNumber = 'unknown', maxAttempts = 5) {
    let attempts = 0;
    
    const cleanup = () => {
      attempts++;
      this.clearCompanyData(phoneNumber, `force-cleanup-attempt-${attempts}`);
      
      setTimeout(() => {
        if (!this.validateCleanState() && attempts < maxAttempts) {
          console.log(`🔄 CacheCleanup: Retry ${attempts + 1}/${maxAttempts} for ${phoneNumber}`);
          cleanup();
        } else if (attempts >= maxAttempts) {
          console.error(`❌ CacheCleanup: Failed to clean after ${maxAttempts} attempts for ${phoneNumber}`);
        } else {
          console.log(`✅ CacheCleanup: Successfully cleaned after ${attempts} attempts for ${phoneNumber}`);
        }
      }, 200);
    };
    
    cleanup();
  }
  /**
   * Check if a phone number should have company data
   * @param {string} phoneNumber - Phone number to check
   * @returns {boolean} - True if this number should have company data
   */
  static shouldHaveCompanyData(phoneNumber) {
    // Known business phone numbers from CSV data in companyDataService.js
    const knownBusinessNumbers = [
      "8698200444", "9920932730", "8928794628", "8238996055", "9820125829",
      "9920057082", "9892033276", "9673542107", "9879529435", "9898704606",
      "9892517650", "9892822717", "9833562524", "9819532607", "9820455298",
      "9820989597", "9049002468", "9819828320", "9167112222", "9987884000",
      "9765407091", "9871919565", "9923009653", "9830304584", "9143400166",
      "7003458141", "9836762136", "9830045663", "9886014880", "9375623124",
      "9825852600", "9831408603", "9836877799", "9004689658", "9824000387",
      "9893055575", "9828020529", "9953006518", "9891940007", "9879000470",
      "9910530280", "9620999098", "9825580650", "9999612300", "9804029250",
      "9810479434", "9699080888", "9818591847", "9841013236", "9830853547",
      "9825433077", "9909006546", "9899771339", "9830320668", "9710444688",
      "9326521524", "9527299997", "9999140643", "9810081835", "9537744669",
      "9879523839", "9052500789", "9840035059", "9000161162", "8807755600",
      "9818306353", "9911836777", "9898146682", "9811231722", "9901028295",
      "9008506009", "9739500149", "8888059888", "9825234576", "9825315128",
      "9841090817", "8448660475", "9841003444", "9818112943", "8130644440",
      "9916917983", "9959874455", "9900175696", "9811422207", "9870727170",
      "8109050888", "9924579249", "9811132851", "9873927810", "9820358271",
      "9810381888", "9958072555", "9811654442", "9811997530", "8890864267",
      "9885442111", "9985920202", "9619450452", "9324494385", "9618969777",
      "9811356905", "9890090003", "9810504880", "9699311945", "7860757696",
      "8819022007", "9246598459", "9655610004", "9701908044", "9346630302",
      "9842266154", "9815001355", "9303760515", "9100910045", "7299961193",
      "9842236932", "9894620198", "9886528016", "9920107670", "9326000731",
      "9581789789", "9313321335", "8059797973", "9737628761", "8904126053",
      "9414066250", "8097743143", "9884256559", "9915911166", "9600655786",
      "9842165456", "8530121590", "9095078290", "9894700358", "7968144500",
      "9728765494", "9819447944", "9688840004", "9367699996", "9842492892",
      "9244643372", "7264954942", "9994497548", "8250999689", "9142555570",
      "9847066188", "9167973112", "9967581535", "9304956022", "9370288258",
      "9087212674", "9004809895", "9831108238", "8971515680", "9911263300",
      "9894586184", "9330955972", "9873500415", "9924373709", "9824440340",
      "9620693338", "9396777220", "9811699895", "9833204108", "9811502175",
      "8447788776", "8428119828", "6381399076", "8428426642", "9841001668",
      "9867671904", "9019541579", "8169921886"
    ];
    
    if (!phoneNumber) return false;
    
    const cleanNumber = phoneNumber.replace(/\D/g, ''); // Remove non-digits
    
    // Always get the last 10 digits (removes country code if present)
    const searchNumber = cleanNumber.slice(-10);
    const isKnownBusiness = knownBusinessNumbers.includes(searchNumber);
    
    console.log(`🔍 CacheCleanup: Phone ${cleanNumber} (searching: ${searchNumber}) should have company data (CSV): ${isKnownBusiness}`);
    return isKnownBusiness;
  }

  /**
   * Initialize cleanup for new session
   * @param {string} phoneNumber - Current phone number
   */
  static initializeForNewSession(phoneNumber) {
    console.log(`🚀 CacheCleanup: Initializing for new session - Phone: ${phoneNumber}`);
    
    // Always start with aggressive cleanup
    this.forceCleanupWithValidation(phoneNumber, 3);
    
    // Set up periodic validation during the session
    const validationInterval = setInterval(() => {
      if (!this.validateCleanState()) {
        console.warn('🚨 CacheCleanup: Detected dirty cache during session, cleaning...');
        this.clearCompanyData(phoneNumber, 'periodic-validation');
      }
    }, 5000); // Check every 5 seconds
    
    // Clear the interval after 2 minutes (form should be completed by then)
    setTimeout(() => {
      clearInterval(validationInterval);
      console.log('🛑 CacheCleanup: Stopped periodic validation');
    }, 120000);
  }
}

export default CacheCleanup;